<template>
  <v-card flat outlined>
    <v-toolbar color="secondary" flat dense>
      <v-toolbar-title>
        <v-icon class="accent--text mr-4">mdi-database-cog-outline</v-icon>
        <span class="accent--text text-h6 text-uppercase">{{
          $t("machines.info.segment.label")
        }}</span></v-toolbar-title
      >
    </v-toolbar>
    <!-- Overlay -->
    <v-overlay
      v-if="showOverlay"
      absolute
      color="#fff"
      opacity=".9"
      class="text--primary"
    >
      <v-row>
        <v-col cols="12" class="d-flex justify-center">
          <h1 class="title mb-5">
            <v-icon color="primary" class="mr-2"
              >mdi-information-outline</v-icon
            >
            {{ $t("machines.info.segment.overlay.heading") }}
          </h1>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex justify-center">
          <p class="mx-8">
            {{ $t("machines.info.segment.overlay.description") }}
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-if="disableCommissioning"
          cols="12"
          class="d-flex justify-center"
        >
          {{ $t("machines.info.segment.overlay.disabledDescription") }}
        </v-col>
        <v-col v-else cols="12" class="d-flex justify-center">
          <v-btn
            @click="showCommissioningGuide = true"
            :disabled="disableCommissioning"
            color="secondary accent--text"
            light
          >
            <v-icon left>mdi-plus-circle</v-icon>
            {{ $t("machines.info.segment.actions.commission") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-overlay>
    <v-card-text>
      <v-row>
        <!-- Segment -->
        <v-col cols="12" md="6" class="py-2">
          <v-select
            v-model="selectedSegment"
            :items="segments"
            item-text="name"
            :label="$t('machines.dialogs.commissioning.steps.segment.name')"
            @input="onSelectedSegment"
            :rules="[validationRules.required]"
            return-object
            :readonly="!isEditing"
            :disabled="!isEditing"
            :append-icon="isEditing ? 'mdi-chevron-down' : null"
          ></v-select>
        </v-col>
        <!-- Category -->
        <v-col cols="12" md="6" class="py-2">
          <v-select
            v-model="selectedCategory"
            :disabled="selectedSegment === null || !isEditing"
            :items="selectedSegment?.categories ?? []"
            item-text="name"
            :label="$t('machines.dialogs.commissioning.steps.category.name')"
            @input="onSelectedCategory"
            :rules="[validationRules.required]"
            return-object
            :readonly="!isEditing"
            :append-icon="isEditing ? 'mdi-chevron-down' : null"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Other category -->
      <v-row v-if="selectedCategory?.canHaveOtherCategory">
        <v-col cols="12">
          <v-text-field
            :label="
              $t(
                'machines.dialogs.commissioning.steps.category.otherCategoryPlaceholder'
              )
            "
            v-model="enteredOtherCategory"
            @input="onEnteredCategoryInput"
            :placeholder="
              $t(
                'machines.dialogs.commissioning.steps.category.otherCategoryPlaceholder'
              )
            "
            :rules="[validationRules.maxLimit, validationRules.name]"
            :readonly="!isEditing"
            :disabled="!isEditing"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Applications -->
      <v-row
        v-if="
          selectedCategory?.applications && (isEditing || selectedApplications)
        "
      >
        <v-col cols="12">
          <v-select
            v-model="selectedApplications"
            :disabled="selectedCategory === null || !isEditing"
            :items="selectedCategory.applications"
            :label="
              $t('machines.dialogs.commissioning.steps.applications.name')
            "
            @input="onSelectedApplications"
            return-object
            :hint="
              isEditing
                ? $t('machines.dialogs.commissioning.steps.applications.hint')
                : ''
            "
            persistent-hint
            multiple
            chips
            :readonly="!isEditing"
            :append-icon="isEditing ? 'mdi-chevron-down' : null"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-text>

    <GuidedMachineCommissioning
      :show="showCommissioningGuide"
      :machineInfo="machineInfo"
      :machine="machine"
      @save="onSaveCommissioning"
      @cancel="onCancelCommissioning"
    />

    <Snackbar
      :show.sync="showSnackbar"
      :text="$t('machines.dialogs.commissioning.saved')"
    />
  </v-card>
</template>

<script>
import GuidedMachineCommissioning from "@/dialogs/GuidedMachineCommissioning"
import Snackbar from "@/components/widgets/Snackbar"

import ValidationRules from "@/utils/validationrules"
let validationRules = null

export default {
  props: ["loading", "machineInfo", "isEditing", "machine"],
  emits: ["update:machineInfo"],
  components: {
    GuidedMachineCommissioning,
    Snackbar
  },
  async created() {
    await this.$store.dispatch("loadSegmentMetadata", "en")
  },
  data: () => ({
    selectedSegment: null,
    selectedCategory: null,
    selectedApplications: null,
    enteredOtherCategory: "",
    showCommissioningGuide: false,
    showSnackbar: false,
    validationRules: validationRules
  }),
  beforeCreate() {
    validationRules = ValidationRules(this.$i18n)
  },
  computed: {
    showOverlay() {
      if (this.machineInfo) {
        return (
          this.machineInfo.isStockMachine &&
          this.machineInfo.commissioningDate === null
        )
      }
      return false
    },
    segmentName() {
      if (this.machineInfo) {
        return this.machineInfo.segmentName
      }
      return ""
    },
    categoryName() {
      let name = ""
      if (this.machineInfo) {
        name = this.machineInfo.categoryName
        if (this.machineInfo.otherCategory?.length > 0) {
          name = `${name} / ${this.machineInfo.otherCategory}`
        }
      }
      return name
    },
    applications() {
      if (this.machineInfo) {
        return this.machineInfo.applications
      }
      return []
    },
    hasApplications() {
      return this.applications && this.applications.length > 0
    },
    disableCommissioning() {
      let hasDeliveryDate = false
      if (this.machineInfo) {
        hasDeliveryDate = this.machineInfo.deliveryDate?.length > 2
      }
      return this.loading || !hasDeliveryDate
    },
    segments() {
      return this.$store.getters.segmentMetadata
    }
  },
  methods: {
    onSaveCommissioning() {
      this.showCommissioningGuide = false
      this.showSnackbar = true

      this.$store.dispatch("fetchMachineInfo", {
        tenantUid: this.machine.tenantUid,
        serialNo: this.machine.serialNo
      })
    },
    onCancelCommissioning() {
      this.showCommissioningGuide = false
    },

    onSelectedSegment(inpSelection) {
      this.selectedSegment = inpSelection

      const updatedModel = {
        ...this.machineInfo,
        segmentId: inpSelection?.id,
        segmentName: inpSelection?.name,

        categoryId: null,
        categoryName: null,
        applications: null,
        otherCategory: ""
      }
      this.$emit("update:machineInfo", updatedModel)
    },
    onSelectedCategory(inpSelection) {
      this.selectedCategory = inpSelection

      const updatedModel = {
        ...this.machineInfo,
        categoryId: inpSelection?.id,
        categoryName: inpSelection?.name,
        applications: null,
        otherCategory: ""
      }
      this.$emit("update:machineInfo", updatedModel)
    },
    onSelectedApplications(inpSelection) {
      this.selectedApplications = inpSelection

      const updatedModel = {
        ...this.machineInfo,
        applications: inpSelection
      }
      this.$emit("update:machineInfo", updatedModel)
    },
    onEnteredCategoryInput(txtInput) {
      this.enteredOtherCategory = txtInput

      const updatedModel = {
        ...this.machineInfo,
        otherCategory: txtInput
      }
      this.$emit("update:machineInfo", updatedModel)
    }
  },
  watch: {
    machineInfo(newVal) {
      if (newVal) {
        const segmentHasChanged = newVal.segmentId != this.selectedSegment?.id
        if (segmentHasChanged) {
          this.selectedSegment = this.segments.find(
            seg => seg.id == newVal.segmentId
          )
        }
        //Note: categoryId is unique in scope of segment but not globally. First category for every segment has the same id = 1
        if (
          segmentHasChanged ||
          newVal.categoryId != this.selectedCategory?.id
        ) {
          this.selectedCategory = this.selectedSegment?.categories.find(
            cat => cat.id == newVal.categoryId
          )
        }
        if (newVal.applications != this.selectedApplications) {
          this.selectedApplications = newVal.applications
        }
        if (newVal.otherCategory != this.enteredOtherCategory) {
          this.enteredOtherCategory = newVal.otherCategory
        }
      }
    }
  }
}
</script>

<style></style>
